import { BaseModel, type RelationConfig } from './base';
import { dateYMDRelation, type DateYMD } from '../lib/dates';

import { Customer } from './customer';
import { CustomerForm } from './customerForm';
import { CustomerPass } from './customerPass';
import { CustomField } from './customField';
import { Employee } from './employee';
import { Integration, IntegrationLabel, IntegrationAction } from './integration';
import { Recurrence } from './recurrence';
import { Reservation, ReservationStatus, ReservationResource } from './reservation';
import { ReservationStatusType } from './reservationStatusType';
import { ServiceResourceAssignment, ServiceResourceDefaultSelection } from './serviceResource';
import { Voucher } from './voucher';

// Helper Classes

export class ReservationCustomerIntegration extends BaseModel {
  integration!: Integration | null;
  labels!: IntegrationLabel[];
  actions!: IntegrationAction[];

  static override relations(): RelationConfig {
    return {
      integration: { type: Integration },
      labels: { type: IntegrationLabel },
      actions: { type: IntegrationAction },
    };
  }
}

export class ReservationCustomerOptions extends BaseModel {
  statusTypes!: ReservationStatusType[];
  availableCustomerPasses!: CustomerPass[];
  availableVouchers!: Voucher[];

  static override relations(): RelationConfig {
    return {
      statusTypes: { type: ReservationStatusType },
      availableCustomerPasses: { type: CustomerPass },
      availableVouchers: { type: Voucher },
    };
  }
}

// Main Model

export class ReservationCustomer extends BaseModel {
  static modelName(): string {
    return 'reservationCustomer'
  }

  idReservationCustomer!: number;
  date!: DateYMD | null;
  status!: ReservationStatus;
  recurrenceGuid!: string | null;
  servicesLabel!: string | null;
  customer!: Customer | null;
  customerPass!: CustomerPass | null;
  customFields!: CustomField[];
  forms!: CustomerForm[];
  options!: ReservationCustomerOptions | null;
  recurrence!: Recurrence | null;
  reservation!: Reservation | null;
  resources!: ReservationResource[];
  statusType!: ReservationStatusType | null;
  validPasses!: CustomerPass[];
  availableCustomerPasses!: CustomerPass[];
  availableVouchers!: Voucher[];
  voucher!: Voucher | null;
  integrations!: ReservationCustomerIntegration[];
  checkedIn!: boolean;
  didNotShow_!: boolean;
  isDeleted!: boolean;
  cancelled!: boolean;

  static override relations(): RelationConfig {
    return {
      customer: { type: Customer },
      customerPass: { type: CustomerPass },
      customFields: { type: CustomField },
      date: dateYMDRelation,
      forms: { type: CustomerForm },
      options: { type: ReservationCustomerOptions },
      recurrence: { type: Recurrence },
      reservation: { type: Reservation },
      resources: { type: ReservationResource },
      statusType: { type: ReservationStatusType },
      validPasses: { type: CustomerPass },
      availableCustomerPasses: { type: CustomerPass },
      availableVouchers: { type: Voucher },
      voucher: { type: Voucher },
      integrations: { type: ReservationCustomerIntegration },
    };
  }

  override objectID(): string | null {
    return this.idReservationCustomer?.toString() ?? null;
  }

  setResources(existingResources: ReservationResource[] | undefined): void {
    const service = (this as any).service;
    const resourcesForAssignment = service?.resources
      ? service.resourcesForAssignment(ServiceResourceAssignment.customer)
      : [];

    const items: ReservationResource[] = [];
    const usedResources: number[] = [];

    for (const serviceResource of resourcesForAssignment) {
      const existing = existingResources
        ?.filter((resource: any) => resource.serviceResource?.idServiceResource === serviceResource.idServiceResource)
        .map((resource: any) => resource.resource);

      for (let i = 0; i < serviceResource.quantity; i++) {
        const reservationResource = new ReservationResource({ serviceResource });
        (reservationResource as any).count = i;

        if (existingResources !== undefined) {
          const available = (existing || [])
            .filter((r: any) => !usedResources.includes(r?.idEmployee));

          (reservationResource as any).resource = available[0] || ServiceResourceDefaultSelection.none;
          if ((reservationResource as any).resource?.idEmployee) {
            usedResources.push((reservationResource as any).resource.idEmployee);
          }
        } else if (serviceResource.defaultSelection === ServiceResourceDefaultSelection.firstAvailable) {
          (reservationResource as any).resource = ServiceResourceDefaultSelection.firstAvailable;
        } else {
          (reservationResource as any).resource = ServiceResourceDefaultSelection.none;
        }

        items.push(reservationResource);
      }
    }

    this.resources = items;
  }

  get _servicesLabel(): string {
    return this.servicesLabel || this.services
      .map((s: any) => s.service?.title)
      .join(', ');
  }

  get employeesLabel(): string {
    const employees = this.services
      .map((s: any) => s.employee?.displayName)
      .filter(Boolean);

    return [...new Set(employees)].join(', ');
  }

  get durationLabel(): number {
    return this.services
      .map((s: any) => s.duration || 0)
      .reduce((a: number, b: number) => a + b, 0);
  }

  get startTime(): number {
    return this.reservation?.startTime as any ?? 0;
  }

  get duration(): number {
    return this.reservation?.duration ?? 0;
  }

  get services(): any[] {
    return this.reservation ? [this.reservation] : [];
  }

  get isStatusConfirmed(): boolean {
    return this.status === ReservationStatus.confirmed;
  }

  get isStatusUnconfirmed(): boolean {
    return this.status === ReservationStatus.unconfirmed;
  }

  get isStatusCancelled(): boolean {
    return this.status === ReservationStatus.cancelled;
  }

  get isRecurring(): boolean {
    return !!this.recurrenceGuid;
  }

  get hasVoucher(): boolean {
    return !!this.voucher && Object.keys(this.voucher).length > 0;
  }

  get hasPass(): boolean {
    return !!this.customerPass;
  }
}
